/// <amd-module name="Dashboards/DashboardView/src/components/headers/ViewModeHeader" />
import * as React from "react";
import { connect } from "react-redux";

import { DashboardViewAwareState } from "../../model/DashboardViewState";
import { DashboardActions } from "../../redux/actions/Actions";
import * as Selectors from "../../redux/selectors/Selectors";
import { ConnectedDashboardPicker } from "./DashboardPicker";

import * as Resources from "Dashboards/DashboardView/Resources";

import { IProjectData, ITeamData } from "Tfs/Platform/Page";
import { ITfsPageDataAwareState } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataContracts";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { TeamPanelIconButton } from "TFS/TeamPanel/TeamPanelIcon/TeamPanelIcon";

import * as String from "VSS/Core/Util/String";

import { Button } from "VSSUI/Button";
import { getFullScreenItem } from "VSSUI/HeaderCommandBar";
import { ConnectedDashboardOptions } from "./DashboardOptions";
import { Dashboard } from "../../model/Dashboard";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { IVssPageContext } from "VSS/Platform/Context";

export interface ViewModeHeaderProps extends ConnectedViewModeHeaderOwnProps, ConnectedViewModeHeaderMappedProps {}

interface ConnectedViewModeHeaderOwnProps {
    onEnterEditClick: () => void;
    dashboard: Dashboard;
    pageContext: IVssPageContext;
}

interface ConnectedViewModeHeaderMappedProps {
    project: IProjectData;
    /**
     * The owning team of the dashboard. Used for the team panel button.
     * Undefined for cases like project dashboards, in which case the team panel button isn't shown.
     */
    dashboardTeamOwner?: ITeamData;
    fullScreen: boolean;
    /** Timestamp of when the dashboard has been rendered last */
    renderTimestamp: number;
    /** Current time */
    currentTimestamp: number;
    /** Affects visibility of the edit/manage dashboard buttons */
    canEditDashboard: boolean;
    /** Affects visibility of refresh time message */
    showRefreshTime: boolean;
}

class ViewModeHeader extends React.Component<ViewModeHeaderProps & typeof Actions> {
    public static getTimeLabelText(showRefreshTime: boolean, currentTimestamp: number, renderTimestamp: number): string {
        const secondsPassed = (currentTimestamp - renderTimestamp) / 1000;

        if (showRefreshTime && secondsPassed != undefined) {
            const minutesPassed = Math.floor(secondsPassed / 60);
            if (minutesPassed === 0) {
                return Resources.Dashboard_Timer_Less_Than_A_Minute_Ago;
            } else if (minutesPassed === 1) {
                return Resources.Dashboard_Timer_Updated_One_Minute_Ago;
            } else if (minutesPassed >= 60) {
                // Stop updating the text if we get passed more than 1 hour
                return Resources.Dashboard_Timer_Updated_More_Than_One_Hour_Ago;
            } else if (minutesPassed > 1) {
                return String.format(Resources.Dashboard_Timer_Updated_X_Minutes_Ago, minutesPassed);
            }
        }

        return "";
    }

    public render(): JSX.Element | null {
        const fullScreenItem = getFullScreenItem(() => {}, this.props.fullScreen);
        return (
            <div className="view-mode-header fontSizeML flex-row flex-noshrink flex-center">
                <ConnectedDashboardPicker />
                {this.props.dashboardTeamOwner && (
                    <TeamPanelIconButton
                        projectId={this.props.project.id}
                        projectName={this.props.project.name}
                        teamId={this.props.dashboardTeamOwner.id}
                        teamName={this.props.dashboardTeamOwner.name}
                    />
                )}

                <div className="view-mode-header-actions fontSizeM flex-row flex-grow flex-center">
                    {this.props.canEditDashboard && (
                        <Button
                            className="edit-dashboard-button"
                            subtle
                            iconProps={{ iconName: "Edit" }}
                            text={Resources.Header_EditText}
                            ariaLabel={Resources.Header_EditAriaLabel}
                            onClick={this.props.onEnterEditClick}
                        />
                    )}
                    <Button
                        className="refresh-dashboard-button"
                        subtle
                        iconProps={{ iconName: "Refresh" }}
                        text={Resources.Header_RefreshText}
                        ariaLabel={Resources.Header_RefreshAriaLabel}
                        onClick={this.onRefreshClick}
                    />
                    <ConnectedDashboardOptions />
                    <div className="flex-row flex-grow flex-center justify-end">
                        {this.getLastRefreshedMessage()}
                        {this.props.canEditDashboard && (
                            <Button
                                className="manage-dashboard"
                                subtle
                                iconProps={{ iconName: "Settings" }}
                                tooltipProps={{ text: Resources.Header_ManageTooltipText }}
                                ariaLabel={Resources.Header_ManageTooltipText}
                                onClick={this.props.onManageSettingsClick}
                            />
                        )}
                        <Button
                            className="fullscreen-dashboard-button"
                            subtle
                            iconProps={fullScreenItem.iconProps}
                            tooltipProps={{ text: fullScreenItem.ariaLabel }}
                            ariaLabel={fullScreenItem.ariaLabel}
                            onClick={this.onToggleFullScreen}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private onToggleFullScreen = (): any => this.props.onToggleFullScreenClick(!this.props.fullScreen);
    private onRefreshClick = (): any => this.props.onRefresh(false);

    private getLastRefreshedMessage() {
        let lastModifiedDetails = this.getUserDetails();
        let timeLabelText = ViewModeHeader.getTimeLabelText(this.props.showRefreshTime, this.props.currentTimestamp, this.props.renderTimestamp);
        return (
            <span className="dashboard-refresh-status">
                <span className="dashboard-lastmodifiedtime">{lastModifiedDetails}</span>
                {(lastModifiedDetails !== "" && timeLabelText !== "") ? " , " : ""}
                {timeLabelText}
            </span>
        );
    }

    private getUserDetails(): string {
        if(!this.props.dashboard.modifiedBy || this.props.dashboard.modifiedBy.trim() === '' || this.props.dashboard.modifiedBy === null || !String.isGuid(this.props.dashboard.modifiedBy)){
            return "";
        }
        try{
            const userDetails = this.props.pageContext.getService<IVssIdentityService>("IVssIdentityService").getIdentityDetails(this.props.dashboard.modifiedBy);
            if (userDetails && this.props.dashboard.modifiedDate > new Date('0001-01-01T00:00:00Z'))
            {
                return String.format(Resources.ModifiedMetadataWithName_Text, ago(this.props.dashboard.modifiedDate, AgoFormat.Compact), userDetails.displayName);
            }
            else if(this.props.dashboard.modifiedDate && this.props.dashboard.modifiedDate > new Date('0001-01-01T00:00:00Z')) {
                return String.format(Resources.ModifiedMetadata_Text, ago(this.props.dashboard.modifiedDate, AgoFormat.Compact));
            }
            else{
                return "";
            }
        }
        catch(e){
            return "";
        }
    }
}

const Actions = {
    onToggleFullScreenClick: DashboardActions.toggleFullScreen,
    onManageSettingsClick: DashboardActions.showDashboardSettingsManager,
    onRefresh: DashboardActions.refreshDashboard
};

function mapStateToProps(state: DashboardViewAwareState & ITfsPageDataAwareState): ConnectedViewModeHeaderMappedProps {
    return {
        project: currentProject(state)!,
        dashboardTeamOwner: Selectors.getDashboardTeamData(state),
        fullScreen: Selectors.showFullScreen(state),
        renderTimestamp: Selectors.getRenderTimestamp(state),
        currentTimestamp: Selectors.getCurrentTimestamp(state),
        canEditDashboard: Selectors.canEditDashboard(state),
        showRefreshTime: Selectors.showRefreshTime(state)
    };
}

export const ConnectedViewModeHeader = connect(mapStateToProps, Actions)(ViewModeHeader);
